<template>
  <ArchiveModal
    :open="open"
    :title="`Manage access · ${cabinet ? cabinet.name : ''}`"
    subtitle="Choose who can open this cabinet and what they can do there."
    size="lg"
    @close="$emit('close')"
  >
    <div class="ar-note">
      <i class="bi bi-info-circle" aria-hidden="true"></i>
      <span>
        People open the Archives through their role: it needs the <strong>Archives</strong> permission in Roles &amp;
        Permissions. Here you choose who sees <strong>this cabinet</strong> and what they can do in it.
        Archives administrators (the Administrator role, or roles with
        <strong>Archives: manage cabinets and access</strong>) open every cabinet without being listed.
      </span>
    </div>

    <form class="ar-add-member" @submit.prevent="add">
      <div class="ar-people-picker">
        <i class="bi bi-person-plus" aria-hidden="true"></i>
        <input
          ref="search"
          v-model="query"
          type="search"
          class="form-control"
          placeholder="Add someone by name or email"
          role="combobox"
          aria-autocomplete="list"
          aria-label="Find someone to add"
          :aria-expanded="showResults && results.length ? 'true' : 'false'"
          @input="queueSearch"
          @focus="showResults = true"
          @blur="hideResultsSoon"
        />
        <ul v-if="showResults && results.length" class="ar-people-results" role="listbox">
          <li
            v-for="p in results"
            :key="p.id"
            role="option"
            :aria-disabled="memberIds.includes(p.id) ? 'true' : null"
            :class="{ disabled: memberIds.includes(p.id) }"
            @mousedown.prevent="choose(p)"
          >
            <span class="ar-avatar">{{ initials(p.name) }}</span>
            <span class="ar-person">
              <strong>{{ p.name }}</strong>
              <small>{{ p.email }}</small>
            </span>
            <span v-if="memberIds.includes(p.id)" class="ar-muted">Already added</span>
            <span v-else-if="p.has_module === false" class="ar-warn-tag" :title="NO_MODULE_HINT">No Archives permission</span>
          </li>
        </ul>
      </div>
      <select v-model="newAccess" class="form-control ar-access-select" aria-label="Access for the person you're adding">
        <option v-for="level in levels" :key="level" :value="level">{{ ACCESS[level].label }}</option>
      </select>
      <button type="submit" class="btn btn-primary" :disabled="!chosen || busy">
        <span v-if="busy === 'add'" class="spinner-border spinner-border-sm mr-1"></span>Add
      </button>
    </form>
    <p class="ar-access-hint">
      <template v-if="chosen"><strong>{{ chosen.name }}</strong> will be a </template>
      <template v-else>A </template>
      <strong>{{ ACCESS[newAccess].label.toLowerCase() }}</strong>: {{ ACCESS[newAccess].hint }}
      <span v-if="chosen && chosen.has_module === false" class="ar-warn-text">
        <i class="bi bi-exclamation-triangle" aria-hidden="true"></i> {{ NO_MODULE_HINT }}
      </span>
    </p>

    <h6 class="ar-subhead">People with access <span class="ar-count">{{ members.length }}</span></h6>
    <div v-if="loading" class="ar-loading"><span class="spinner-border spinner-border-sm mr-1"></span> Loading…</div>
    <ul v-else class="ar-members">
      <li v-for="m in members" :key="m.id">
        <span class="ar-avatar">{{ initials(m.name) }}</span>
        <span class="ar-person">
          <strong>{{ m.name }}<span v-if="m.is_you" class="ar-you">you</span></strong>
          <small>{{ m.email || "" }}</small>
          <small v-if="m.has_module === false" class="ar-warn-text" :title="NO_MODULE_HINT">
            <i class="bi bi-exclamation-triangle" aria-hidden="true"></i> Can't open the Archives yet: their role needs
            the Archives permission
          </small>
        </span>
        <select
          :value="m.access"
          class="form-control form-control-sm ar-access-select"
          :aria-label="`Access for ${m.name}`"
          :disabled="busy === m.id"
          @change="change(m, $event)"
        >
          <option v-for="level in levels" :key="level" :value="level">{{ ACCESS[level].label }}</option>
        </select>
        <button
          type="button"
          class="ar-icon-btn danger"
          :aria-label="`Remove ${m.name}`"
          title="Remove access"
          :disabled="busy === m.id"
          @click="remove(m)"
        >
          <i class="bi bi-x-lg"></i>
        </button>
      </li>
      <li v-if="!members.length" class="ar-empty-line">No one has been added yet. Search above to add people.</li>
    </ul>
  </ArchiveModal>
</template>

<script>
import axios from "axios";
import ArchiveModal from "./ArchiveModal.vue";
import { ACCESS, ACCESS_ORDER, errorMessage } from "../archiveHelpers";

const NO_MODULE_HINT =
  "Their role doesn't have the Archives permission yet, so they won't see this cabinet until an administrator adds it in Roles & Permissions.";

/** Who can open a cabinet, and at what level (viewer, contributor, manager). */
export default {
  name: "ArchiveMembersModal",
  components: { ArchiveModal },
  props: {
    open: { type: Boolean, default: false },
    cabinet: { type: Object, default: null },
  },
  data() {
    return {
      ACCESS,
      NO_MODULE_HINT,
      levels: ACCESS_ORDER,
      members: [],
      loading: false,
      busy: null,
      query: "",
      results: [],
      showResults: false,
      chosen: null,
      newAccess: "viewer",
    };
  },
  computed: {
    memberIds() {
      return this.members.map((m) => m.user_id);
    },
  },
  watch: {
    open(value) {
      if (!value) return;
      this.query = "";
      this.results = [];
      this.chosen = null;
      this.newAccess = "viewer";
      this.load();
    },
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
  },
  methods: {
    initials(name) {
      return String(name || "?")
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0].toUpperCase())
        .join("");
    },
    async load() {
      this.loading = true;
      try {
        const { data } = await axios.get(`api/archives/cabinets/${this.cabinet.id}/members`);
        this.members = data.members || [];
      } catch (err) {
        toastr.error(await errorMessage(err, "Couldn't load who has access."), "Access");
      } finally {
        this.loading = false;
      }
    },
    queueSearch() {
      this.chosen = null;
      this.showResults = true;
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(this.searchPeople, 250);
    },
    async searchPeople() {
      const q = this.query.trim();
      const ticket = (this.searchTicket = (this.searchTicket || 0) + 1);
      try {
        const { data } = await axios.get("api/archives/users", { params: { q } });
        if (ticket === this.searchTicket) this.results = data || [];
      } catch (err) {
        if (ticket === this.searchTicket) this.results = [];
      }
    },
    hideResultsSoon() {
      setTimeout(() => {
        this.showResults = false;
      }, 150);
    },
    choose(person) {
      if (this.memberIds.includes(person.id)) return;
      this.chosen = person;
      this.query = person.name;
      this.showResults = false;
    },
    async add() {
      if (!this.chosen || this.busy) return;
      this.busy = "add";
      try {
        const { data } = await axios.post(`api/archives/cabinets/${this.cabinet.id}/members`, {
          user_id: this.chosen.id,
          access: this.newAccess,
        });
        if (data.has_module === false) toastr.warning(data.message, "Access", { timeOut: 9000 });
        else toastr.success(data.message, "Access");
        this.chosen = null;
        this.query = "";
        this.results = [];
        await this.load();
        this.$emit("changed");
        this.$nextTick(() => this.$refs.search && this.$refs.search.focus());
      } catch (err) {
        toastr.error(await errorMessage(err, "Couldn't add them."), "Access");
      } finally {
        this.busy = null;
      }
    },
    async change(member, event) {
      const access = event.target.value;
      if (member.is_you && member.access === "manager" && access !== "manager") {
        if (!window.confirm("You'll no longer be able to manage this cabinet. Continue?")) {
          event.target.value = member.access;
          return;
        }
      }
      this.busy = member.id;
      try {
        const { data } = await axios.put(`api/archives/cabinets/${this.cabinet.id}/members/${member.id}`, { access });
        toastr.success(data.message, "Access");
        member.access = access;
        this.$emit("changed");
      } catch (err) {
        event.target.value = member.access;
        toastr.error(await errorMessage(err, "Couldn't change their access."), "Access");
      } finally {
        this.busy = null;
      }
    },
    async remove(member) {
      const question = member.is_you
        ? "Remove your own access to this cabinet?"
        : `Remove ${member.name}'s access to ${this.cabinet.name}?`;
      if (!window.confirm(question)) return;
      this.busy = member.id;
      try {
        const { data } = await axios.delete(`api/archives/cabinets/${this.cabinet.id}/members/${member.id}`);
        toastr.success(data.message, "Access");
        this.members = this.members.filter((m) => m.id !== member.id);
        this.$emit("changed");
      } catch (err) {
        toastr.error(await errorMessage(err, "Couldn't remove their access."), "Access");
      } finally {
        this.busy = null;
      }
    },
  },
};
</script>
